/**
 * Unified geolocation helper.
 *
 * Inside Capacitor (Android / iOS native app) the browser's
 * `navigator.geolocation` API is blocked. We use `@capacitor/geolocation`
 * which triggers the proper native runtime permission dialog on Android.
 * On the web it falls back to `navigator.geolocation` so the PWA keeps working.
 */
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

const isNative = () => {
  try {
    return Capacitor.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

/**
 * Get the current position.
 * @param {{ enableHighAccuracy?: boolean, timeout?: number, maximumAge?: number }} options
 * @returns {Promise<{ coords: { latitude: number, longitude: number, accuracy: number } }>}
 */
export async function getCurrentPosition(options = {}) {
  const opts = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 30000,
    ...options,
  };

  if (isNative()) {
    // Ask for permission first — required on Android 6+ (runtime perm)
    try {
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
        const req = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (req.location !== 'granted' && req.coarseLocation !== 'granted') {
          const err = new Error('Location permission denied');
          err.code = 1; // match W3C PositionError.PERMISSION_DENIED
          throw err;
        }
      }
    } catch (e) {
      // If permissions API itself fails, let getCurrentPosition try anyway
      console.warn('[geo] permissions check failed', e?.message);
    }

    const pos = await Geolocation.getCurrentPosition(opts);
    // Capacitor returns { coords: { latitude, longitude, accuracy, ... }, timestamp }
    return pos;
  }

  // Web fallback
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const err = new Error('Geolocation not supported');
      err.code = 2;
      return reject(err);
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

/**
 * Check whether we can probably ask for location at all (without triggering a prompt).
 */
export function isGeolocationAvailable() {
  if (isNative()) return true; // Capacitor plugin always available in native builds
  return typeof navigator !== 'undefined' && !!navigator.geolocation;
}
